import { Component, inject, Injector, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { ApportionmentPreviewComponent } from '@app/component/dialog/apportionment-preview/apportionment-preview.component';
import { PaymentCondition } from '@app/interface/paymentCondition';
import { PaymentService } from '@app/service/payment.service';
import { APPORTIONMENT_CODE, APPORTIONMENTS } from '@app/shared/enums/apportionment';
import { RegEspTrib } from '@app/shared/types/fiscal/reg-esp-trib';
import { catchError, firstValueFrom, Observable, of, startWith, switchMap, timeout } from 'rxjs';
import { SelectUtilities } from 'src/app/class/select-utilities';
import { UnsubscribeAll } from 'src/app/class/unsubscribe-all';
import { Utilities } from 'src/app/class/utilities';
import { ConfirmationDialogComponent } from 'src/app/component/dialog/confirmation-dialog/confirmation-dialog.component';
import { COPY_MATRIX_STOCK_DIALOG_MESSAGE_ID, CopyMatrixStockDialogComponent } from 'src/app/component/dialog/copy-matrix-stock-dialog/copy-matrix-stock-dialog.component';
import { PhotoEditComponent } from 'src/app/component/dialog/photo-edit/photo-edit.component';
import { City } from 'src/app/interface/city';
import { Company, COMPANY_STATE, INVOICE_IMPORT_TYPE, PROFIT_CALC_TYPE, SEND_CODE, TYPE_VEHICLE } from 'src/app/interface/company';
import { DEFAULT_PART_PRICE_READJUSTMENT_MODE } from 'src/app/interface/part-price-readjustment-mode';
import { CompanySavePayload } from 'src/app/interface/company-save-payload';
import { Operation } from 'src/app/interface/operation';
import { AddressService } from 'src/app/service/address.service';
import { CompanyService } from 'src/app/service/company.service';
import { BankReconciliationService } from '@app/service/bank-reconciliation.service';
import { ReconciliationConfirmDialogComponent } from '@app/component/page/company/financial/bank-reconciliation/confirm-dialog/confirm-dialog.component';
import { TP_DIALOG_CONFIG } from '@app/component/page/company/financial/bank-reconciliation/bank-reconciliation.format';
import { LaborService } from 'src/app/service/labor.service';
import { PartService } from 'src/app/service/part.service';
import { LayoutService } from 'src/app/service/layout.service';
import { OperationService } from 'src/app/service/operation.service';
import { SnackbarService } from 'src/app/service/snackbar.service';
import { StorageUrlService } from 'src/app/service/storage-url.service';
import { BOOLEAN_ONE } from 'src/app/shared/common-enums/boolean-one';
import { BOOLEAN_TWO } from 'src/app/shared/common-enums/boolean-two';
import { companyBrands } from 'src/app/shared/lists/company-brands';
import { COMPANY_TAB, companyTabs } from 'src/app/shared/lists/company-tabs';
import { NATUREZAS_DE_OPERACAO } from 'src/app/shared/lists/naturezas-de-operacao';
import {
  groupNfsePatterns,
  NFSE_PATTERN,
  SIGCORP_MARILIA_NATURE_CODES,
  SIGCORP_MARILIA_NATURE_HINT,
  SIGCORP_MARILIA_PATTERN
} from 'src/app/shared/lists/nfse-patterns';
import { REGIM_ESPEC_TRIBUT_PREST } from 'src/app/shared/lists/reg_esp_trib_pres';
import { DocumentPipe } from 'src/app/shared/pipes/document.pipe';
import { ObjectId } from 'src/app/shared/type-aliases/object-id';
import { environment } from 'src/environments/environment';

const SERIE_RGX = /^[1-9]{1}[0-9]{0,2}$|^0{1}$/;
const NFSE_SERIE_RGX = /^\w{1,5}$/;
const DIGITS_ONLY_RGX = /^\d+$/;
const AUTO_CODE_PARAMS = [
  { flag: 'autoCodeParts', next: 'autoCodePartsNext' },
  { flag: 'autoCodeLabors', next: 'autoCodeLaborsNext' },
] as const;
const AUTO_CODE_FIRST_VALUE = '0001';
const FISCAL_VALIDATORS: { [key in keyof Partial<Company>]: ValidatorFn[] } = {
  tributMunicipio: [Validators.required],
  lastNSU: [Validators.required],
  accessKeyInvoice: [Validators.required],
  crt: [Validators.required, Validators.min(1), Validators.max(4)],
  stateRegistration: [Validators.required],
  IM: [Validators.required],
  cnaeCode: [Validators.required, Validators.minLength(6), Validators.maxLength(10)],
  numberInvoice: [Validators.maxLength(9)],
  numberInvoiceNFC: [Validators.maxLength(9)],
  numberInvoiceNFS: [Validators.maxLength(15)],
  numberGerencial: [Validators.maxLength(9)],
  serieInvoice: [Validators.minLength(1), Validators.maxLength(3), Validators.pattern(SERIE_RGX), Utilities.differentValues(['serieInvoiceNFC', 'serieInvoiceNFS', 'serieGerencial'])],
  serieInvoiceNFC: [Validators.minLength(1), Validators.maxLength(3), Validators.pattern(SERIE_RGX), Utilities.differentValues(['serieInvoice', 'serieInvoiceNFS', 'serieGerencial'])],
  serieInvoiceNFS: [Validators.minLength(1), Validators.maxLength(5), Validators.pattern(NFSE_SERIE_RGX), Utilities.differentValues(['serieInvoice', 'serieInvoiceNFC', 'serieGerencial'])],
  serieGerencial: [Validators.minLength(1), Validators.maxLength(3), Validators.pattern(SERIE_RGX), Utilities.differentValues(['serieInvoice', 'serieInvoiceNFC', 'serieInvoiceNFS'])]
}

@Component({
  selector: 'app-company-detail',
  templateUrl: './company-detail.component.html',
  styleUrl: './company-detail.component.scss'
})
export class CompanyDetailComponent extends UnsubscribeAll implements OnInit, OnDestroy {
  @ViewChild(MatTabGroup, { static: false }) matTabGroup: MatTabGroup;
  currentTabIndex = 0;
  totalTabs = 5;

  SelectUtilities = SelectUtilities;
  readonly APPORTIONMENTS = APPORTIONMENTS;

  identifycationForm = new FormGroup({
    id: new FormControl<ObjectId>(''),
    socialName: new FormControl('', [Validators.required, Validators.maxLength(60), Validators.minLength(2)]),
    fancyName: new FormControl('', [Validators.required, Validators.maxLength(60)]),
    cnpj: new FormControl('', [Validators.required, Utilities.validateCNPJ()]),
    cnpjMatrix: new FormControl('', Utilities.validateCNPJ()),
    companyLogo: new FormControl(''),
    associateLogo: new FormControl(''),
    companyLogoInput: new FormControl(''),
    associationLogoInput: new FormControl('')
  }, {
    validators: [
      Utilities.distinctControls('cnpj', 'cnpjMatrix') // both CNPJ should be distinct
    ]
  })

  addressForm = new FormGroup({
    cep: new FormControl('', [Validators.minLength(8), Validators.required]),
    street: new FormControl('', [Validators.minLength(2), Validators.maxLength(60), Validators.required]),
    number: new FormControl('', Validators.required),
    complement: new FormControl(''),
    neighborhood: new FormControl('', [Validators.required, Validators.minLength(2), Validators.maxLength(60)]),
    city: new FormControl('', Validators.required),
    state: new FormControl('', Validators.required),
    type: new FormControl('', Validators.required)
  });
  stateCities$: Observable<City[]>;

  paramsForm = new FormGroup({
    controlStock: new FormControl<Company['controlStock']>(false),
    discountControl: new FormControl<Company['discountControl']>(BOOLEAN_TWO.FALSE),
    complementaryOS: new FormControl<Company['complementaryOS']>(BOOLEAN_TWO.TRUE),
    newMenu: new FormControl<Company["newMenu"]>(BOOLEAN_TWO.FALSE),
    osCount: new FormControl<Company['osCount']>(BOOLEAN_TWO.FALSE),
    openOS: new FormControl<Company['openOS']>(BOOLEAN_ONE.TRUE),
    costAlert: new FormControl<Company['costAlert']>(BOOLEAN_TWO.FALSE),
    sendCode: new FormControl<Company['sendCode']>(SEND_CODE.ALLOW_SEND_CODE),
    controlFinc: new FormControl<Company['controlFinc']>(false),
    useNewBankReconciliation: new FormControl<Company['useNewBankReconciliation']>(false),
    typeVehicle: new FormControl<Company["typeVehicle"]>(TYPE_VEHICLE.CAR),
    mecLabors: new FormControl<Company["mecLabors"]>(false),
    mecParts: new FormControl<Company["mecParts"]>(false),
    useAvarias: new FormControl<Company["useAvarias"]>(false),
    checklistSim: new FormControl<Company["checklistSim"]>(false),
    profitCalcType: new FormControl<Company["profitCalcType"]>(PROFIT_CALC_TYPE.AVERAGE_COST),
    useUnifiedServices: new FormControl<Company["useUnifiedServices"]>(false),
    descriptionForUnifyServices: new FormControl<Company["descriptionForUnifyServices"]>(""),
    labelGenerator: new FormControl<Company["labelGenerator"]>(false),
    saturdayIsWorkingDay: new FormControl<Company["saturdayIsWorkingDay"]>(false),
    applyLowByRo: new FormControl<Company["applyLowByRo"]>(false),
    apportionment: new FormControl<Company['apportionment']>(APPORTIONMENT_CODE.ROUND_AUTO_FIRST),
    applyLowByOrder: new FormControl<Company['applyLowByOrder']>(false),
    blockOverdueClients: new FormControl<Company['blockOverdueClients']>(false),
    useMultipleMechanics: new FormControl<Company["useMultipleMechanics"]>(false),
    useNewOsPanel: new FormControl<Company["useNewOsPanel"]>(false),
    linkPartsToLabors: new FormControl<Company["linkPartsToLabors"]>(false),
    invoiceImportType: new FormControl<Company['invoiceImportType']>(INVOICE_IMPORT_TYPE.CONSULTA),
    autoCodeParts: new FormControl<Company['autoCodeParts']>(false),
    autoCodePartsNext: new FormControl<Company['autoCodePartsNext']>('', Validators.pattern(DIGITS_ONLY_RGX)),
    autoCodeLabors: new FormControl<Company['autoCodeLabors']>(false),
    autoCodeLaborsNext: new FormControl<Company['autoCodeLaborsNext']>('', Validators.pattern(DIGITS_ONLY_RGX)),
    partPriceReadjustmentMode: new FormControl<Company['partPriceReadjustmentMode']>(DEFAULT_PART_PRICE_READJUSTMENT_MODE),
  });

  fiscalForm = new FormGroup({
    useTaxReform: new FormControl<Company['useTaxReform']>(false),
    omitISSValue: new FormControl<Company['omitISSValue']>(false),
    deduNFSE: new FormControl<Company['deduNFSE']>(false),
    deduNFE: new FormControl<Company['deduNFE']>(false),
    deduISS: new FormControl<Company['deduISS']>(false),
    crt: new FormControl<Company['crt']>(undefined, FISCAL_VALIDATORS.crt),
    stateRegistration: new FormControl('', FISCAL_VALIDATORS.stateRegistration),
    IM: new FormControl('', FISCAL_VALIDATORS.IM),
    tributMunicipio: new FormControl('', FISCAL_VALIDATORS.tributMunicipio),
    iteListServico: new FormControl<Company["iteListServico"]>(''),
    regEspTrib: new FormControl<RegEspTrib>(undefined),
    verificaRPS: new FormControl<Company['verificaRPS']>(''),
    exigibilidade: new FormControl<Company['exigibilidade']>(undefined),
    exigibilidadeRetIss: new FormControl<Company['exigibilidadeRetIss']>(undefined),
    munIncidISS: new FormControl<Company['munIncidISS']>('Não informa'),
    ISS: new FormControl<Company['ISS']>(0),
    numberInvoice: new FormControl('', FISCAL_VALIDATORS.numberInvoice),
    numberInvoiceNFC: new FormControl('', FISCAL_VALIDATORS.numberInvoiceNFC),
    numberInvoiceNFS: new FormControl('', FISCAL_VALIDATORS.numberInvoiceNFS),
    numberGerencial: new FormControl('', FISCAL_VALIDATORS.numberGerencial),
    minISS: new FormControl<Company["minISS"]>(0),
    serieInvoice: new FormControl<Company['serieInvoice']>(undefined, FISCAL_VALIDATORS.serieInvoice),
    serieInvoiceNFC: new FormControl<Company['serieInvoiceNFC']>(undefined, FISCAL_VALIDATORS.serieInvoiceNFC),
    serieInvoiceNFS: new FormControl<Company['serieInvoiceNFS']>(undefined, FISCAL_VALIDATORS.serieInvoiceNFS),
    serieGerencial: new FormControl<Company['serieGerencial']>(undefined, FISCAL_VALIDATORS.serieGerencial),
    cnaeCode: new FormControl('', FISCAL_VALIDATORS.cnaeCode),
    lastNSU: new FormControl('', FISCAL_VALIDATORS.lastNSU),
    accessKeyInvoice: new FormControl('', FISCAL_VALIDATORS.accessKeyInvoice),
    migrateIntegrated: new FormControl<Company['migrateIntegrated']>(true), // true by default
    patternNFSE: new FormControl<NFSE_PATTERN>("NFS-e Nacional"),
    authorized: new FormControl<Company['authorized']>([]),
    cancelDate: new FormControl<number>(undefined),
    operationEstorno: new FormControl<ObjectId>(undefined),
    operationEstornoOut: new FormControl<ObjectId>(undefined),
    sendPisCofinsHighlightValue: new FormControl<Company["sendPisCofinsHighlightValue"]>(false),
    sendfPagNFSe: new FormControl<Company["sendfPagNFSe"]>(false),
  });

  contactForm = new FormGroup({
    phone: new FormControl(''),
    phone2: new FormControl(''),
    email: new FormControl('', Validators.email),
    site: new FormControl(''),
    tokenWhats: new FormControl(''),
    crmApiUrl: new FormControl('')
  })

  formArray = new FormArray([
    this.identifycationForm,
    this.addressForm,
    this.contactForm,
    this.fiscalForm,
    this.paramsForm,
  ])

  TYPE_VEHICLE = TYPE_VEHICLE;
  isAgro = environment.agro;
  isNautical = environment.nautical;
  openDialog: MatDialogRef<ConfirmationDialogComponent> | MatDialogRef<PhotoEditComponent>;
  companyLogoBlob: Blob;
  associationLogoBlob: Blob;
  companyBrands = companyBrands;
  companyTabs = companyTabs;
  COMPANY_TAB = COMPANY_TAB;
  BOOLEAN_ONE = BOOLEAN_ONE;
  BOOLEAN_TWO = BOOLEAN_TWO;
  SEND_CODE = SEND_CODE;
  INVOICE_IMPORT_TYPE = INVOICE_IMPORT_TYPE;
  PROFIT_CALC_TYPE = PROFIT_CALC_TYPE;
  regEspTribList = REGIM_ESPEC_TRIBUT_PREST;
  NFSE_PATTERN_GROUPS = groupNfsePatterns();
  readonly SIGCORP_MARILIA_PATTERN = SIGCORP_MARILIA_PATTERN;
  readonly SIGCORP_MARILIA_NATURE_HINT = SIGCORP_MARILIA_NATURE_HINT;
  NATUREZAS = NATUREZAS_DE_OPERACAO.filter(nat => {
    return [1, 2, 3, 4, 10, 11].includes(nat.code) // default values
  });
  isMakena = environment.makena;
  companyData: Company;
  authorizatedCpfAndCpnjInput = new FormControl("");
  isTcarIntegrated = new FormControl<boolean>(false);
  isMobile: boolean;

  checkboxClasses = ['col-12', 'col-md-6', 'col-lg-4', 'col-lg-3', 'col-xl-2', 'col-xxl-1'];
  searchingForCep: boolean = false;

  private _operationService = inject(OperationService);
  private _storageUrlService = inject(StorageUrlService);
  private _partService = inject(PartService);
  private _laborService = inject(LaborService);
  private _injector = inject(Injector);
  inboundOperations: Operation[] = [];
  outboundOperations: Operation[] = [];
  loaderSgn = signal(false);

  constructor(
    protected addressService: AddressService,
    private _companyService: CompanyService,
    private _layout: LayoutService,
    private _snackbar: SnackbarService,
    private _dialog: MatDialog,
    private _router: Router,
    private _activatedRoute: ActivatedRoute
  ) {
    super();
  }

  ngOnInit() {
    this._updateTabsCount(this.isTcarIntegrated.value);

    this.subs.push(this.isTcarIntegrated.valueChanges
      .subscribe(isTcarIntegrated => this._updateTabsCount(isTcarIntegrated)));

    for (const param of AUTO_CODE_PARAMS) {
      this.subs.push(this.paramsForm.controls[param.flag].valueChanges
        .subscribe(checked => {
          this._syncAutoCodeNextValidators();
          if (checked) {
            void this._fillNextCodeFromHistory(param);
          }
        }));
    }

    // find address on input cep
    this.subs.push(this.addressForm.get('cep').valueChanges
      .subscribe(async (value: string) => {
        const cep = Utilities.removeMask(value);
        if (cep.length === 8) {
          this.searchingForCep = true
          try {
            const address = await firstValueFrom(this.addressService.searchCep(cep));

            this.searchingForCep = false;

            if (address) {
              this.addressForm.get('number').setValue('');
              this.addressForm.get('complement').setValue(address.complement);
              this.addressForm.get('street').setValue(address.street);
              this.addressForm.get('neighborhood').setValue(address.neighborhood);
              this.addressForm.get('state').setValue(address.state);
              this.addressForm.get('city').setValue(address.city);
            } else {
              this.addressForm.get('cep').setErrors({ cepNotFound: true });
              this.addressForm.get('cep').markAsTouched();
            }
          } catch (error) {
            console.error(error);
            this.addressForm.get('cep').setErrors({ cepError: true });
            this.addressForm.get('cep').markAsTouched();
            this.searchingForCep = false;
          }
        }
      }));

    if (!environment.makena) {
      // Update fiscal validations according to migrateIntegrated
      this.subs.push(this.fiscalForm.get('migrateIntegrated').valueChanges
        .subscribe(migrateIntegrated => this._handleMigrateIntegratedChanges(migrateIntegrated)));

      this.subs.push(this.isTcarIntegrated.valueChanges
        .subscribe(tcarIntegrated => {
          if (tcarIntegrated) {
            // TCar-integrated companies can't be Migrate-integrated
            this.fiscalForm.get('migrateIntegrated').setValue(false);
          }
        }));

      // the <tributMunicipio> tag is required for some NFSE patterns
      this.subs.push(this.fiscalForm.get('patternNFSE').valueChanges
        .subscribe((value: Company['patternNFSE']) => {

          switch (value) {
            case "FISCO – SGA":
              this.regEspTribList = REGIM_ESPEC_TRIBUT_PREST;
              this.NATUREZAS = NATUREZAS_DE_OPERACAO.filter(nat => [1, 2, 3, 4, 5, 6, 7, 8].includes(nat.code));
              break;
            case "Governo Digital":
              this.regEspTribList = REGIM_ESPEC_TRIBUT_PREST;
              this.NATUREZAS = NATUREZAS_DE_OPERACAO.filter(nat => [3, 4, 5, 6, 7, 8, 9].includes(nat.code));
              break;
            case "Embras Siap.Net":
            case "Síntese Tecnologia":
            case "Futurize":
            case "RLZ":
              this.regEspTribList = REGIM_ESPEC_TRIBUT_PREST.slice(1, 7);
              this.NATUREZAS = NATUREZAS_DE_OPERACAO.filter(nat => [7, 8, 3, 9, 4, 5, 6].includes(nat.code));
              break;
            case "SIGCORP ABRASF":
            case "SMARAPDSil WS2":
            case "HM2 Soluções":
            case "WEB ISS 2.0":
            case "TIPLAN 2.0":
              this.regEspTribList = REGIM_ESPEC_TRIBUT_PREST;
              this.NATUREZAS = NATUREZAS_DE_OPERACAO.filter(nat => [7, 8, 3, 9, 4, 5, 6].includes(nat.code));
              break;
            case SIGCORP_MARILIA_PATTERN:
              this.regEspTribList = REGIM_ESPEC_TRIBUT_PREST;
              this.NATUREZAS = NATUREZAS_DE_OPERACAO.filter(nat =>
                SIGCORP_MARILIA_NATURE_CODES.some(code => code === nat.code)
              );
              break;
            case "IPM":
            case "IPM (Araranguá)":
            case "IPM (Brusque)":
              this.regEspTribList = REGIM_ESPEC_TRIBUT_PREST;
              this.NATUREZAS = NATUREZAS_DE_OPERACAO.filter(nat => [1, 2, 3, 4, 10, 11, 16, 20, 21, 22, 23, 24, 25, 26].includes(nat.code));
              break;
            case "D2TI":
              this.regEspTribList = REGIM_ESPEC_TRIBUT_PREST;
              this.NATUREZAS = NATUREZAS_DE_OPERACAO.filter(nat => [3, 4, 5, 1, 2, 12, 10, 16, 17].includes(nat.code));
              break;
            case "Assessor Público":
              this.regEspTribList = REGIM_ESPEC_TRIBUT_PREST;
              this.NATUREZAS = NATUREZAS_DE_OPERACAO.filter(nat => [12, 14, 16, 17, 1].includes(nat.code));
              break;
            case "Assessor Público (Lins-SP)":
              this.regEspTribList = REGIM_ESPEC_TRIBUT_PREST;
              this.NATUREZAS = NATUREZAS_DE_OPERACAO.filter(nat => [1, 3, 4, 8, 12, 14, 16, 17, 42].includes(nat.code));
              break;
            case "Assessor Público (Votorantim-SP)":
              this.regEspTribList = REGIM_ESPEC_TRIBUT_PREST;
              this.NATUREZAS = NATUREZAS_DE_OPERACAO.filter(nat => [3, 4, 7, 10, 12, 14, 17].includes(nat.code));
              break;
            case "NFPSe WS":
              this.NATUREZAS = NATUREZAS_DE_OPERACAO.filter(nat => [1, 3, 4, 8, 10, 11, 12, 15, 16, 27].includes(nat.code))
              break;
            case "THEMA":
              this.NATUREZAS = NATUREZAS_DE_OPERACAO.filter(nat => [2, 5, 10, 12, 16, 51, 52, 54, 57, 58, 63, 64, 67, 68, 69].includes(nat.code))
              break;
            case "Pública":
              this.NATUREZAS = NATUREZAS_DE_OPERACAO.filter(nat => [1, 2, 3, 4, 10, 12, 15, 17, 50, 52, 501, 502, 601, 701, 107].includes(nat.code))
              break;
            case "Conam (e-transparência)":
              this.regEspTribList = REGIM_ESPEC_TRIBUT_PREST;
              this.NATUREZAS = NATUREZAS_DE_OPERACAO.filter(nat => [1, 3, 5, 7, 10, 12, 13].includes(nat.code));
              break;
            default:
              this.regEspTribList = REGIM_ESPEC_TRIBUT_PREST;
              this.NATUREZAS = NATUREZAS_DE_OPERACAO.filter(nat => [1, 2, 3, 4, 10].includes(nat.code));
              break;
          }

          if (value === "Futurize") {
            // CNAE is optional for some majors with pattern Futurize
            this.fiscalForm.get('cnaeCode').removeValidators(Validators.required);
            this.fiscalForm.get('cnaeCode').updateValueAndValidity();
          } else {
            this.fiscalForm.get('cnaeCode').setValidators(FISCAL_VALIDATORS.cnaeCode);
            this.fiscalForm.get('cnaeCode').updateValueAndValidity();
          }

          if (value === 'IPM (Araranguá)') {
            this.fiscalForm.get('tributMunicipio').setValidators(null);
            this.fiscalForm.get('tributMunicipio').updateValueAndValidity();
          } else {
            this.fiscalForm.get('tributMunicipio').setValidators(FISCAL_VALIDATORS.tributMunicipio);
            this.fiscalForm.get('tributMunicipio').updateValueAndValidity();
          }
        }));
    }

    this.subs.push(this._layout.isHandset$.subscribe(isMobile => this.isMobile = isMobile));

    this.getData();
  }

  private _handleMigrateIntegratedChanges(isMigrateIntegrated: boolean) {
    if (isMigrateIntegrated) {
      // TCar-integrated companies can't emit invoices via Mekanic
      this.isTcarIntegrated.setValue(false, { emitEvent: false });

      // get operations for refund invoices
      if (!this.inboundOperations.length && !this.outboundOperations.length) {
        const cnpj = this.identifycationForm.value.cnpjMatrix || this.identifycationForm.value.cnpj;
        this._operationService.getAll(cnpj).then(operations => {
          this.inboundOperations = operations.filter(op => op.available && op.entry);
          this.outboundOperations = operations.filter(op => op.available && !op.entry);
        });
      }
    }

    for (const key in FISCAL_VALIDATORS) {
      const control = this.fiscalForm.get(key);
      if (!control) {
        console.warn(`Control with name "${key}" not found!`);
        continue;
      }

      if (isMigrateIntegrated) {
        control.addValidators(FISCAL_VALIDATORS[key]);
      } else {
        control.removeValidators(FISCAL_VALIDATORS[key]);
      }
      control.updateValueAndValidity();
    }
  }

  ngOnDestroy() {
    this.companyLogoBlob = null;
    this.associationLogoBlob = null;
    this.unsubscribeAll();
  }

  private async getData() {
    this.loaderSgn.set(true);
    try {
      const companyId = this._activatedRoute.snapshot.params['company'];
      if (companyId) {
        const company = await this._companyService.get(companyId, true);
        this.companyData = company;
        this.identifycationForm.patchValue(company, { emitEvent: false });
        this._lockCnpjMatrixIfAlreadySet(company.cnpjMatrix);
        this.addressForm.patchValue(company.address, { emitEvent: false });
        this.contactForm.patchValue(company, { emitEvent: false });
        this.fiscalForm.patchValue(company, { emitEvent: false });

        this.fiscalForm.controls.patternNFSE.patchValue(company.patternNFSE, { emitEvent: true });

        this.paramsForm.patchValue(company, { emitEvent: false });
        this._syncAutoCodeNextValidators();
        await firstValueFrom(this._storageUrlService.preload([company.companyLogo, company.associateLogo], company.cnpj));
        await this.patchInvoiceImportTypeFromMigrate(company);
        this.isTcarIntegrated.setValue(company?.currentState == COMPANY_STATE.integrated);

        if (company.migrateIntegrated && !this.inboundOperations.length && !this.outboundOperations.length) {
          const cnpj = this.identifycationForm.value.cnpjMatrix || this.identifycationForm.value.cnpj;
          this._operationService.getAll(cnpj).then(operations => {
            this.inboundOperations = operations.filter(op => op.available && op.entry);
            this.outboundOperations = operations.filter(op => op.available && !op.entry);
          });
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.loaderSgn.set(false)
      this.stateCities$ = this.addressForm.get('state').valueChanges.pipe(
        startWith(this.addressForm?.value?.state),
        switchMap(stateId => this.addressService.getStateCities(stateId))
      );
    }
  }

  /**
   * `cnpjMatrix` records which matriz this filial belongs to and must never change once set —
   * editing it here can silently desync it from the matriz's own `cnpj`, breaking matrix-fallback
   * lookups such as historical OS-type resolution (see task #72885).
   */
  private _lockCnpjMatrixIfAlreadySet(cnpjMatrix: string): void {
    if (cnpjMatrix?.trim()) {
      this.identifycationForm.controls.cnpjMatrix.disable({ emitEvent: false });
    }
  }

  private async patchInvoiceImportTypeFromMigrate(company: Company) {
    try {
      const invoiceImportType = await this._companyService.getInvoiceImportType(company);
      this.paramsForm.controls.invoiceImportType.patchValue(invoiceImportType, { emitEvent: false });
    } catch (error) {
      console.warn('Não foi possível carregar o tipo de importação de notas no Migrate', error);
      this.paramsForm.controls.invoiceImportType.patchValue(INVOICE_IMPORT_TYPE.CONSULTA, { emitEvent: false });
    }
  }

  public onInputCNPJ() {
    const cnpjInput = this.identifycationForm.get('cnpj');
    const validStr = cnpjInput.value.replace(/\D/g, '');
    cnpjInput.setValue(validStr);
  }

  onChangeState() {
    this.addressForm.get('city').setValue('');
  }

  onClick(element: HTMLInputElement) {
    element.click();
  }

  async onChangeCompanyLogo(event: Event) {
    const files = (event.target as any).files as FileList;
    if (!files.length) {
      return;
    }
    const file = files[0];
    if (!Utilities.isImage(file)) {
      return;
    }
    this.openDialog = this._dialog
      .open(PhotoEditComponent, { disableClose: true, data: { file: file, ratio: 3 } });
    const canvas: HTMLCanvasElement = await this.openDialog.afterClosed().toPromise();
    this.openDialog = null;
    if (!canvas) {
      return;
    }
    this.companyLogoBlob = await Utilities.toBlob(canvas);
    const oldCanvas = document.getElementById('company-logo-canvas') as HTMLCanvasElement;
    canvas.id = oldCanvas.id;
    canvas.classList.add('photo');
    oldCanvas.replaceWith(canvas);
  }

  public async onChangeAssociationLogo(event: Event) {
    const files = (event.target as any).files as FileList;
    if (!files.length) {
      return;
    }
    const file = files[0];
    if (!Utilities.isImage(file)) {
      return;
    }
    this.openDialog = this._dialog
      .open(PhotoEditComponent, { disableClose: true, data: { file: file, ratio: 3 } });
    const canvas: HTMLCanvasElement = await this.openDialog.afterClosed().toPromise();
    this.openDialog = null;
    if (!canvas) {
      return;
    }
    this.associationLogoBlob = await Utilities.toBlob(canvas);
    const oldCanvas = document.getElementById('association-logo-canvas') as HTMLCanvasElement;
    canvas.id = oldCanvas.id;
    canvas.onclick = oldCanvas.onclick;
    canvas.classList.add('photo');
    oldCanvas.replaceWith(canvas);
  }

  public async deleteCompanyLogo() {
    this.openDialog = this._dialog
      .open(ConfirmationDialogComponent, { disableClose: true, data: { text: 'DELETE_IMAGE' } });
    const result: boolean = await this.openDialog.afterClosed().toPromise();
    this.openDialog = null;
    if (result) {
      document.getElementById('company-logo-canvas').classList.add('hidden');
      this.identifycationForm.get('companyLogo').setValue('');
      this.companyLogoBlob = null;
    }
  }

  public async deleteAssociationLogo() {
    this.openDialog = this._dialog.open(ConfirmationDialogComponent, {
      disableClose: true,
      data: { text: 'DELETE_IMAGE' }
    });
    const result: boolean = await this.openDialog.afterClosed().toPromise();
    this.openDialog = null;
    if (result) {
      document.getElementById('association-logo-canvas').classList.add('hidden');
      this.identifycationForm.get('associateLogo').setValue('');
      this.associationLogoBlob = null;
    }
  }

  /**
   * Flag da nova conciliação bancária. Os dois avisos só orientam (a flag é livre): ao ligar numa empresa com
   * Conta Azul, e ao desligar uma empresa com contas já ativadas. Cancelar o aviso devolve o checkbox ao valor anterior.
   */
  async onNewBankReconciliationToggle(checked: boolean) {
    const control = this.paramsForm.controls.useNewBankReconciliation;
    const warning = checked ? this._newBankReconciliationEnableWarning() : await this._newBankReconciliationDisableWarning();
    if (warning) {
      const confirmed = await firstValueFrom(
        this._dialog
          .open(ReconciliationConfirmDialogComponent, { ...TP_DIALOG_CONFIG, data: warning })
          .afterClosed(),
      );
      if (!confirmed) {
        control.setValue(!checked);
        return;
      }
    }
    control.setValue(checked);
    this.paramsForm.markAsDirty();
  }

  private _newBankReconciliationEnableWarning() {
    if (Number(this.companyData?.currentState) !== COMPANY_STATE.CONTA_AZUL) {
      return null;
    }
    return {
      title: 'Empresa com Conta Azul',
      lines: ['Empresas com Conta Azul ainda não estão no piloto da nova conciliação bancária.', 'Você pode ligar a flag mesmo assim; este aviso é só uma orientação.'],
      confirmLabel: 'Ligar mesmo assim',
      icon: 'warning_amber',
      tone: 'warning' as const,
    };
  }

  private async _newBankReconciliationDisableWarning() {
    // Só há contas ativadas se a flag já estava ligada no servidor (a API recusa a consulta sem ela).
    if (!this.companyData?.useNewBankReconciliation) {
      return null;
    }
    const accounts = await firstValueFrom(
      // Resolvido na hora: só este aviso precisa do serviço (e das suas dependências HTTP).
      this._injector.get(BankReconciliationService).listAccounts(this.companyData.cnpj).pipe(timeout(8000), catchError(() => of(null))),
    );
    // Sem conseguir consultar, o aviso aparece por precaução.
    const hasActivated = accounts ? accounts.accounts.some((account) => account.activation?.status === 'active') : true;
    if (!hasActivated) {
      return null;
    }
    return {
      title: 'Desligar a nova conciliação?',
      lines: [
        'As movimentações já conciliadas continuam bloqueadas para edição.',
        'Para desfazer conciliações, a nova conciliação precisa estar ligada.',
      ],
      confirmLabel: 'Desligar mesmo assim',
      icon: 'warning_amber',
      tone: 'warning' as const,
    };
  }

  public async onSubmit() {
    let company: CompanySavePayload;
    const identifycation = this.identifycationForm.getRawValue();
    const address = this.addressForm.getRawValue();
    const params = !this.isTcarIntegrated.value ? this.paramsForm.getRawValue() : {
      newMenu: this.paramsForm.controls?.newMenu?.value,
      sendCode: this.paramsForm.controls?.sendCode?.value,
      typeVehicle: this.paramsForm.controls?.typeVehicle?.value,
    };
    const fiscal = !this.isTcarIntegrated.value ? this.fiscalForm.getRawValue() : {} as any;
    const contact = this.contactForm.getRawValue();
    company = {
      ...identifycation,
      address,
      ...params,
      ...fiscal,
      ...contact,
    }

    const param = { cnpj: identifycation.cnpj };
    const isUpdate = Boolean(company?.id);

    if (isUpdate && this._isLinkingToMatrix(identifycation.cnpjMatrix)) {
      const copyMatrixStock = await this._askCopyMatrixStock();
      if (copyMatrixStock === undefined) {
        return;
      }
      company.copyMatrixStock = copyMatrixStock;
    }

    company.cnpj = this.addCnpjMask(company.cnpj);

    try {
      this._layout.loader = true;
      company.companyLogoBlob = this.companyLogoBlob;
      company.associationLogoBlob = this.associationLogoBlob;
      if (isUpdate) {
        await this._companyService.update(company)
        if (!this.isTcarIntegrated.value) {
          await this._companyService.updateInvoiceImportType(
            company,
            this.paramsForm.controls.invoiceImportType.value || INVOICE_IMPORT_TYPE.CONSULTA
          ).catch(e => console.error("Error on update invoice import type", { e }));
        }
        await this._companyService.reloadLoggedCompany().catch(e => console.error("Error on reload logged company", { e }));
      } else {
        await this._companyService.add(company);
      }
      this.goBack()
      this._snackbar.success(isUpdate ? 'COMPANY.UPDATED' : 'COMPANY.ADDED', param);
    } catch (error) {
      this._snackbar.error(isUpdate ? 'COMPANY.NOT_UPDATED' : 'COMPANY.NOT_ADDED', param, error);
    } finally {
      this._layout.loader = false;
    }
  }

  private _isLinkingToMatrix(cnpjMatrix: string): boolean {
    return Boolean(cnpjMatrix?.trim()) && !this.companyData?.cnpjMatrix?.trim();
  }

  private _askCopyMatrixStock(): Promise<boolean | undefined> {
    const dialogRef = this._dialog.open<CopyMatrixStockDialogComponent, void, boolean>(
      CopyMatrixStockDialogComponent,
      { ariaDescribedBy: COPY_MATRIX_STOCK_DIALOG_MESSAGE_ID }
    );
    return firstValueFrom(dialogRef.afterClosed());
  }

  addCnpjMask(cnpj: string) {
    return new DocumentPipe().transform(cnpj);
  }

  goBack() {
    this._router.navigate(["company"])
  }

  addNewCpfOrCnpj(value: string) {
    value = value.replaceAll(/\D/g, "");

    if (this.validateCpfCnpjValue(value, 11)) {
      this.addAuthorizedItem(value);
    } else if (this.validateCpfCnpjValue(value, 14)) {
      this.addAuthorizedItem(value);
    } else {
      this._snackbar.error("COMPANY.INVALID_DOCUMENT_ADD");
    }
  }

  addAuthorizedItem(value: string) {
    const authorizedPreviousValue = this.fiscalForm.get('authorized').value;
    if (authorizedPreviousValue.length === 10) {
      this._snackbar.error("COMPANY.MAX_REGISTERS_SIZE")
      return;
    }
    this.fiscalForm.get('authorized').setValue([...authorizedPreviousValue, value]);
    this.fiscalForm.markAsDirty();
  }

  removeAuthorizedItem(index: number) {
    const authorizedPreviousValue = this.fiscalForm.get('authorized').value;

    (authorizedPreviousValue as []).splice(index, 1);

    this.fiscalForm.get('authorized').setValue(authorizedPreviousValue)
    this.fiscalForm.markAsDirty();
  }

  toggleEdit(htmlElement: HTMLDivElement, cancel = false) {
    if (cancel) {
      htmlElement.classList.add("d-none");
      htmlElement.classList.remove("d-flex");
      return;
    }

    if (htmlElement.classList.contains("d-none")) {
      htmlElement.classList.remove("d-none");
      htmlElement.classList.add("d-flex");
    } else if (htmlElement.classList.contains("d-flex")) {
      htmlElement.classList.add("d-none");
      htmlElement.classList.remove("d-flex");
    }
  }

  editCpfOrCnpj(index: number, { value, htmlElement }: { value: string, htmlElement: HTMLDivElement }) {
    value = value.replaceAll(/\D/g, "");
    const authorizedPreviousValue = this.fiscalForm.get('authorized').value;

    if (this.validateCpfCnpjValue(value, 11)) {
      authorizedPreviousValue[index] = value;
      htmlElement.classList.add("d-none");
      htmlElement.classList.remove("d-flex");
    } else if (this.validateCpfCnpjValue(value, 14)) {
      authorizedPreviousValue[index] = value;
      htmlElement.classList.add("d-none");
      htmlElement.classList.remove("d-flex");
    } else {
      this._snackbar.error("COMPANY.INVALID_DOCUMENT_UPDATE");
    }
  }

  validateCpfCnpjValue(value: string, length: number): boolean {
    return value.length === length && !value.match(/[a-zA-Z!@#$%&*()\/\+]/g)
  }

  generateIntegrationKey() {
    this._companyService.generateIntegrationTokenFor({ cnpj: this.identifycationForm.get('cnpj').value })
      .subscribe({
        next: (key) => this._dialog.open(ConfirmationDialogComponent, {
          data: {
            text: "KEY_GENERATED",
            param: { key },
            uniqueAction: true
          },
          maxWidth: this.isMobile ? "95vw" : "30rem",
        }),
        error: (error) => this._snackbar.error("COMPANY.ERROR_GENERATE_KEY", null, error)
      });
  }

  handleTabNavigation(goBack: boolean) {
    setTimeout(() => {
      if (goBack) {
        this.matTabGroup.selectedIndex--;
      } else {
        this.matTabGroup.selectedIndex++;
      }
    }, 10)
  }

  onTabChange(event: MatTabChangeEvent) {
    this.currentTabIndex = event.index;
  }

  private _updateTabsCount(isTcarIntegrated: boolean) {
    this.totalTabs = isTcarIntegrated ? 4 : 5;
    if (this.currentTabIndex > this.totalTabs - 1) {
      this.currentTabIndex = this.totalTabs - 1;
      if (this.matTabGroup) {
        this.matTabGroup.selectedIndex = this.currentTabIndex;
      }
    }
  }

  handleProfitCalcType(checked: boolean) {
    this.paramsForm.get('profitCalcType').setValue(checked ? PROFIT_CALC_TYPE.AVERAGE_COST : PROFIT_CALC_TYPE.LAST_COST);
    this.paramsForm.markAsDirty();
  }

  onAutoCodeNextInput(next: (typeof AUTO_CODE_PARAMS)[number]['next'], event: Event) {
    const value = (event.target as HTMLInputElement).value;
    if (!value) {
      this.paramsForm.controls[next].setValue('');
    }
  }

  private async _fillNextCodeFromHistory({ flag, next }: (typeof AUTO_CODE_PARAMS)[number]) {
    const nextControl = this.paramsForm.controls[next];
    if (nextControl.value) {
      return;
    }

    let code = AUTO_CODE_FIRST_VALUE;
    const savedCnpj = this.companyData?.id ? this.companyData.cnpj : undefined;
    if (savedCnpj) {
      try {
        code = flag === 'autoCodeParts'
          ? await this._partService.getNextCodeFromHistory(savedCnpj)
          : await this._laborService.getNextCodeFromHistory(savedCnpj);
      } catch (error) {
        console.warn('Não foi possível obter o próximo código pelo histórico', error);
        return;
      }
    }

    if (code && !nextControl.value && this.paramsForm.controls[flag].value) {
      nextControl.setValue(code);
    }
  }

  private _syncAutoCodeNextValidators() {
    for (const { flag, next } of AUTO_CODE_PARAMS) {
      const nextControl = this.paramsForm.controls[next];
      const validators: ValidatorFn[] = [Validators.pattern(DIGITS_ONLY_RGX)];
      if (this.paramsForm.controls[flag].value) {
        validators.push(Validators.required);
      }
      nextControl.setValidators(validators);
      nextControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  validateAndFocusFirstError() {
    if (this.formArray.pristine && !this.formArray.invalid) {
      return;
    }

    this.formArray.controls.forEach(form => {
      form.markAllAsTouched();
      form.updateValueAndValidity();
    });

    const formToTabIndex = [0, 1, 2, 3, 4];

    for (let i = 0; i < this.formArray.controls.length; i++) {
      const form = this.formArray.controls[i] as FormGroup;
      if (form.invalid) {
        const invalidControl = this._findFirstInvalidControl(form);
        if (invalidControl) {
          if (this.matTabGroup && typeof this.matTabGroup.selectedIndex === 'number') {
            this.matTabGroup.selectedIndex = formToTabIndex[i];
          }
          setTimeout(() => {
            const element = document.querySelector(`[formcontrolname="${invalidControl}"]`);
            if (element && typeof (element as HTMLElement).focus === 'function') {
              (element as HTMLElement).focus();
            }
          }, 100);
        }
        break;
      }
    }
  }

  private _findFirstInvalidControl(form: FormGroup): string | null {
    for (const key of Object.keys(form.controls)) {
      const control = form.get(key);
      if (control.invalid) {
        return key;
      }
    }
    return null;
  }

  handleClickOnUnifiedServiceHelper(event: Event, unifiedServiceHelper: MatTooltip) {
    event.preventDefault();
    event.stopPropagation();

    setTimeout(() => unifiedServiceHelper.show(), 10)
  }

  private paymentConditionService = inject(PaymentService);
  conditions: PaymentCondition[] = [];
  async getConditions() {
    if (!this.conditions.length) {
      const cnpj = this.identifycationForm.controls.cnpj.value;
      console.log("Buscando condições de pagamento", cnpj)
      this.conditions = await this.paymentConditionService.getAll(cnpj);
    }
    return this.conditions;
  }

  async apportionMentHelper() {
    const data = await this.getConditions();
    this._dialog.open(ApportionmentPreviewComponent, { data })
  }
}
